import React from "react";

/**
 * Renders markdown content as formatted HTML.
 * Uses simple regex-based rendering (no heavy markdown library dependency).
 * All HTML is text-only — no raw HTML injection.
 */
export default function MarkdownBlock({ payload }: { payload: Record<string, unknown> }) {
  const content = typeof payload.content === "string" ? payload.content : "";

  // Simple markdown-to-HTML conversion (safe — no raw HTML passthrough).
  const rendered = content
    // Code blocks
    .replace(/```(\w*)\n([\s\S]*?)```/g, (_m, _lang, code) => {
      const escaped = escapeHtml(code.trim());
      return `<pre style="background:var(--c-surface-3);padding:10px;border-radius:6px;overflow-x:auto;font-size:12px;border:1px solid var(--c-border)"><code>${escaped}</code></pre>`;
    })
    // Inline code
    .replace(/`([^`]+)`/g, (_m, code) => {
      return `<code style="background:var(--c-surface-3);padding:1px 4px;border-radius:3px;font-size:12px">${escapeHtml(code)}</code>`;
    })
    // Bold
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    // Italic
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    // Headers
    .replace(/^### (.+)$/gm, '<h4 style="margin:12px 0 4px;font-size:14px">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 style="margin:14px 0 4px;font-size:15px">$1</h3>')
    .replace(/^# (.+)$/gm, '<h2 style="margin:16px 0 6px;font-size:17px">$1</h2>')
    // Unordered lists
    .replace(/^[*-] (.+)$/gm, '<li style="margin-left:16px">$1</li>')
    // Ordered lists
    .replace(/^\d+\. (.+)$/gm, '<li style="margin-left:16px;list-style-type:decimal">$1</li>')
    // Line breaks
    .replace(/\n/g, "<br/>");

  return (
    <div
      className="text-sm leading-relaxed"
      style={{ color: "var(--c-text)" }}
      dangerouslySetInnerHTML={{ __html: rendered }}
    />
  );
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
